<template>
  <form class="multi-step-form" :id="formStore.formElement" @submit.prevent="handleSubmit">
    <div class="Card">
      <slot :name="`step-${currentStep}`" :on-validity-changed="handleValidityChange"></slot>

      <div class="Card-content">
        <div class="u-flex u-flexRow u-flexJustifyBetween">
          <button
            type="button"
            @click="prevStep"
            :disabled="visibleSteps.indexOf(currentStep) === 0"
            class="Button Button--subtle"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              aria-hidden="true"
              class="Icon u-textGrow1 u-textColorInherit"
              focusable="false"
              role="presentation"
            >
              <polyline
                transform="rotate(180,12,12)"
                points="8,5 16,12 8,19"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                fill="none"
              ></polyline>
            </svg>
            <span> Previous</span>
          </button>
          <button
            v-if="visibleSteps.indexOf(currentStep) < visibleSteps.length - 1"
            type="button"
            @click="nextStep"
            class="Button Button--subtle"
            :disabled="!formStore.isCurrentStepValid"
          >
            <span>Next </span>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              aria-hidden="true"
              class="Icon u-textGrow1 u-textColorInherit"
              focusable="false"
              role="presentation"
            >
              <polyline
                points="8,5 16,12 8,19"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                fill="none"
              ></polyline>
            </svg>
          </button>
          <button
            v-else
            type="submit"
            @click="handleSubmit"
            class="Button Button--primary"
            :disabled="!allStepsValid || isSubmitting"
          >
            {{ isSubmitting ? 'Submitting...' : 'Submit Application' }}
          </button>
        </div>
        <!-- Error display -->
        <div v-if="submitError" class="error-message">
          {{ submitError }}
        </div>
      </div>
    </div>
  </form>
</template>

<script setup>
import { onMounted, computed, watch } from 'vue'
import { useFormStore } from '../stores/formStore'
import { useFormSubmission } from '../composables/useFormSubmission'

const formStore = useFormStore()

const { submitForm, isSubmitting, submitError /* submitSuccess, resetSubmissionState */ } = useFormSubmission()

const props = defineProps({
  totalSteps: {
    type: Number,
    required: true,
  },
})

// Computed property for the actual step numbers that should be shown
const visibleSteps = computed(() => {
  return formStore.requiredSteps
})

const emit = defineEmits(['form-submitted', 'step-changed'])

// Ensure formStore is properly initialized on mount
onMounted(() => {
  // Initialize application type from URL parameters first
  formStore.initializeFromUrlParams()

  if (!formStore.currentStep || formStore.currentStep < 1) {
    formStore.setCurrentStep(1)
  }
  console.log('MultiStepForm mounted, current step:', formStore.currentStep)
})

// Use the formStore as the single source of truth for current step
const currentStep = computed({
  get: () => formStore.currentStep,
  set: (value) => {
    formStore.setCurrentStep(value)
  },
})

// Computed property to check if all steps are valid
const allStepsValid = computed(() => {
  return formStore.allStepsValid
})

// Watch for invalid step values and correct them
watch(
  () => formStore.currentStep,
  (newStep) => {
    if (newStep < 1) {
      console.warn('Invalid step detected, correcting to step 1')
      formStore.setCurrentStep(1)
    } else if (newStep > props.totalSteps) {
      console.warn(`Invalid step detected, correcting to step ${props.totalSteps}`)
      formStore.setCurrentStep(props.totalSteps)
    }
  },
)

// Handler for the event emitted by the child step component
function handleValidityChange(isValid) {
  formStore.setStepValidation(formStore.currentStep, isValid)
}

function nextStep(e) {
  e.preventDefault()

  const currentStepIndex = visibleSteps.value.indexOf(currentStep.value)
  const hasNextStep = currentStepIndex < visibleSteps.value.length - 1

  if (formStore.isCurrentStepValid && hasNextStep) {
    console.log('Current step is valid, moving to next step')
    // Update stepValidation for current step before moving
    formStore.setStepValidation(currentStep.value, true)

    // Move to next visible step
    const nextStepValue = visibleSteps.value[currentStepIndex + 1]
    currentStep.value = nextStepValue
    emit('step-changed', nextStepValue)

    console.log(`Moved to step ${nextStepValue}, validity:`, formStore.isCurrentStepValid)
  } else {
    console.log(
      'Cannot move to next step. Valid:',
      formStore.isCurrentStepValid,
      'Current step:',
      currentStep.value,
      'Has next:',
      hasNextStep,
    )
  }
}

function prevStep(e) {
  // Prevent any default button behavior
  if (e) {
    e.preventDefault()
  }

  const currentStepIndex = visibleSteps.value.indexOf(currentStep.value)
  const hasPrevStep = currentStepIndex > 0

  if (hasPrevStep) {
    // Move to previous visible step
    const prevStepValue = visibleSteps.value[currentStepIndex - 1]
    currentStep.value = prevStepValue
    emit('step-changed', prevStepValue)

    console.log('Moved back to step', prevStepValue)
  }
}

// Handle form submission
const handleSubmit = async () => {
  // Emit form submitted event
  emit('form-submitted')

  const result = await submitForm()

  if (result.success) {
    // Form submitted successfully, user will be redirected
    console.log('Form submitted successfully!')
  } else {
    // Handle error - submitError.value will contain the error message
    console.error('Submission failed:', result.error)
  }
}
</script>
